<template>
  <GuDropdownMenu>
    <GuDropdownMenuTrigger as-child>
      <GuButton
        :disabled="cantPerformBulkAction"
        :loading="requestInProgress"
        variant="secondary"
        size="sm"
        :aria-label="`Bulk actions for datagrid`"
      >
        Bulk Action
      </GuButton>
    </GuDropdownMenuTrigger>

    <GuDropdownMenuContent>
      <GuDropdownMenuItem
        v-for="action in actions"
        :key="action.label"
        :disabled="action.disabled || requestInProgress"
        :data-link-to="getBulkActionUrl(action)"
        @click="action.disabled || requestInProgress ? null : onBulkActionClick(action)"
      >
        <span v-if="action.visible">{{ action.label }}</span>
      </GuDropdownMenuItem>
    </GuDropdownMenuContent>
  </GuDropdownMenu>

  <GuToastProvider />
</template>

<script setup>
import { computed, inject, onMounted, onUnmounted, ref } from 'vue'

import { DOCUMENTATION_URLS } from '@shared/constants/urls'
import { isCondition, parseCondition } from '@shared/utils/json-logic'
import { warn } from '@shared/global/console-messages'
import { startBulkAction, getBulkActionUrl } from '@gu-datagrid-new/api/datagrid.api'

import { GuButton } from '@shared/gu-components/button'
import {
  GuDropdownMenu,
  GuDropdownMenuContent,
  GuDropdownMenuTrigger,
  GuDropdownMenuItem,
} from '@shared/gu-components/dropdown-menu'
import { GuToastProvider, useToast } from '@shared/gu-components/toast'

const props = defineProps({
  gridApi: {
    type: Object,
    required: true,
  },
  settings: {
    type: Object,
    required: true,
  },
})

const gridState = inject('gridState')
const pageVarsQuery = inject('pageVarsQuery')
const { toast } = useToast()

const requestInProgress = ref(false)

const cantPerformBulkAction = computed(() => gridState.selectedRows.value.length < 1)

const onBulkActionClick = async (action) => {
 if (action.type === 'externalTrigger') {
  const externalElement = document.getElementById(action.triggerElementId)
  if (externalElement) externalElement.click()
  return
 }

  const appIds = gridState.selectedRows.value.flat().join(',')

  requestInProgress.value = true
  try {
    const response = await startBulkAction(action, appIds)

    if (response?.navigate_to) {
      window.location.href = response.navigate_to
    } else {
      requestInProgress.value = false
    }
  } catch (error) {
    const status = error.response?.status
    const statusText = error.response?.statusText
    let description

    if (status) {
      description = `Request failed with status code ${status}${statusText ? ` - ${statusText}` : ''}`
    } else {
      description = 'Error occurred processing bulk action request'
    }

    toast({
      description: description,
      variant: 'danger',
      dismissable: true,
      duration: Infinity,
    })
    requestInProgress.value = false
  }
}

const actions = computed(() => {
  let actionsList

  if (props?.settings?.bulkActions?.length) {
    actionsList = props.settings.bulkActions.map(action => {
      let visible = true

      if (action?.visible === false) return

      if (action.visible && action.visible !== false) {
        const hasCondition = isCondition(action.visible)
        const user = pageVarsQuery.pageVars?.value?.user || window.siteVars?.user || {}

        const shouldBeVisible = hasCondition ? parseCondition(action.visible, { user }) : true

        if (shouldBeVisible === false) return

        visible = shouldBeVisible
      }

      return {
        ...action,
        visible,
      }
    }).filter(Boolean)
  }

  return actionsList?.length
    ? actionsList
    : [{ label: 'No actions available', disabled: true, visible: true }]
})

onMounted(() => {
  if (!props.gridApi) return

  if (!props?.settings?.bulkActions) {
    warn({
      context: 'Bulk Actions',
      message: 'No bulk actions defined. Define actions in `multirowSelectSettings`',
      link: DOCUMENTATION_URLS.GU_DATAGRID_V3,
    })
  }

  gridState.updateSelectedRows(props.gridApi)
  props.gridApi.addEventListener('selectionChanged', gridState.updateSelectedRows)
})

onUnmounted(() => {
  if (!props.gridApi) return

  props.gridApi.removeEventListener('selectionChanged', gridState.updateSelectedRows)
})
</script>
